import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useIsFetching } from '@tanstack/react-query'
import KNDataTable from 'components/KNDataTable/KNDataTable'
import KNDataTableOverflow from 'components/KNDataTable/KNDataTableOverflow'
import type { KNDataTableAction, KNDataTableColumn } from 'components/KNDataTable/types'
import type { Company } from 'context/authentication/Company.types'
import { Role } from 'context/authentication/Role.types'
import { useCompaniesView } from 'context/CompaniesViewContext'
import { analyticsEvent } from 'global/helpers/analytics'
import { hasAccess } from 'global/helpers/authorization'
import useDialog from 'global/hooks/useDialog'
import { type ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { COMPANIES_QUERY_KEY } from './CompanyManager.service'
import CompanyTypeLabel from './CompanyTypeLabel'
import CreateOrEditCompanyDialog from './CreateOrEditCompanyDialog'
import DeleteCompanyDialog from './DeleteCompanyDialog'

interface CompanyTableProps {
  readonly companies: Company[]
  readonly pageCount?: number
}

const CompanyTable = ({ companies, pageCount }: CompanyTableProps): ReactElement => {
  const { t } = useTranslation()
  const { page, setPage } = useCompaniesView()
  const [activeCompany, setActiveCompany] = useState<Company>()
  const createOrEditCompanyDialog = useDialog<typeof CreateOrEditCompanyDialog>()
  const deleteCompanyDialog = useDialog<typeof DeleteCompanyDialog>()
  const isFetching = useIsFetching({ queryKey: [COMPANIES_QUERY_KEY] })

  const handleEditClick = useCallback((row: Company) => {
    setActiveCompany(row)
    createOrEditCompanyDialog.handleOpen()
    analyticsEvent('polestar_cv_edit_company_button_clicked')
  }, [])

  const handleDeleteClick = useCallback((row: Company) => {
    setActiveCompany(row)
    deleteCompanyDialog.handleOpen()
    analyticsEvent('polestar_cv_delete_company_button_clicked')
  }, [])

  const displayNameColumnValue = (row: Company) => {
    return <Stack data-test="company-name" spacing={1} direction="row" alignItems="center">
      <Typography component="p" variant="p3" color="text.main">{row.displayName}</Typography>
      <CompanyTypeLabel type={row.type} />
      {row.properties?.visibilityLevel && (
        <Typography variant="label" color="primary.light">
          {t(`screens.shared.company_manager.visibility_types.${row.properties.visibilityLevel}`)}
        </Typography>
      )}
    </Stack>
  }

  const customerIdentifierColumnValue = (row: Company) => {
    return <KNDataTableOverflow
      row={row}
      label={t('general.view_all')}
      header={t('screens.shared.company_manager.card.columns.customer_ids')}
      items={row.customerIdentifiers ?? []}
      rightAligned
    />
  }

  const columns = useMemo((): KNDataTableColumn<Company>[] => [
    {
      name: 'displayName',
      label: t('screens.shared.company_manager.card.columns.display_name'),
      getValue: displayNameColumnValue,
      sx: { width: { xs: '24rem', xxl: 'auto' } },
    },
    {
      name: 'customerIdentifier',
      label: t('screens.shared.company_manager.card.columns.customer_ids'),
      getValue: customerIdentifierColumnValue,
      sx: { width: '24rem' },
    },
  ], [])

  const actions = useMemo((): KNDataTableAction<Company>[] => [
    {
      name: 'edit',
      label: t('screens.shared.company_manager.card.actions.edit'),
      icon: <EditIcon />,
      onClick: handleEditClick,
    },
    {
      name: 'delete',
      label: t('screens.shared.company_manager.card.actions.delete'),
      icon: <DeleteIcon />,
      onClick: handleDeleteClick,
    },
  ], [])

  return (
    <>
      <KNDataTable
        columns={columns}
        actions={hasAccess(Role.Admin) ? actions : []}
        data={companies}
        page={page}
        pageCount={pageCount}
        onPageChange={setPage}
        isLoading={isFetching > 0}
        sx={{
          marginX: -2,
        }}
      />
      {activeCompany && hasAccess(Role.Admin) && (
        <>
          <CreateOrEditCompanyDialog
            payload={{
              company: activeCompany,
            }}
            open={createOrEditCompanyDialog.isOpen}
            onAction={createOrEditCompanyDialog.handleAction}
            onClose={createOrEditCompanyDialog.handleClose}
          />
          <DeleteCompanyDialog
            payload={{
              company: activeCompany,
            }}
            open={deleteCompanyDialog.isOpen}
            onAction={deleteCompanyDialog.handleAction}
            onClose={deleteCompanyDialog.handleClose}
          />
        </>
      )}
    </>
  )
}

export default CompanyTable
